import { useCallback, useEffect, useMemo, useState } from 'react';
import videojs, { VideoJsPlayer } from 'video.js';

import { useLogger } from '../../../contexts/logger-context';
import { useKollective } from '../p2p/use-kollective';
import { useVBrick } from '../p2p/use-vbrick';
import { PlayerError } from '../player-errors';
import { PlayerSources } from '../player.config';
import { Plugins } from '../player.interface';
import { getSources } from '../utils/get-sources';
import { setPlayerSrc } from '../utils/set-player-src';

const Tech = {
  kollective: 'kollective',
  vbrick: 'vbrick',
  hive: 'hive',
  html5: 'html5',
} as const;

export function useSetSource(
  player: VideoJsPlayer,
  sources: PlayerSources,
  plugins: Plugins,
  autoplay: boolean,
  preferredQuality?: string
): {
  sourceIsSet: boolean;
  reloadSource: () => void;
} {
  const { logger: mainLogger } = useLogger();
  const logger = useMemo(() => mainLogger.getSubLogger({ name: 'useSetSource' }), [mainLogger]);

  const [sourceIsSet, setSourceIsSet] = useState(false);

  const setVideoSrc = useCallback(
    async (videoSources: videojs.Tech.SourceObject[], force?: boolean) => {
      logger.info('Try to set the video sources:', videoSources);

      // empty sources are not allowed, to trigger an player error
      if (!videoSources) {
        logger.info('No video sources provided, skipping source setting.');
        return;
      }

      if (!force) {
        const currentSrc = player.currentSrc();
        const hasVideoSrc = videoSources.find((src) => src.src === currentSrc);

        // Do not allow setting the same source again to avoid unnecessary reloading
        if (hasVideoSrc) {
          logger.info('Source is already set, skipping source setting.');
          return;
        }
      }

      try {
        setSourceIsSet(false);
        logger.info('Setting video sources:', videoSources);
        await setPlayerSrc(player, videoSources, autoplay);
        logger.info('Source set successfully.');
        setSourceIsSet(true);
      } catch (error) {
        logger.error('Error setting video sources:', error);
        if (error instanceof PlayerError) {
          player.error(error.message);
        }
      }
    },
    [logger, autoplay, player]
  );

  const tech = useMemo(() => {
    if (!sources.hls) {
      logger.info('No HLS source provided, defaulting to HTML5 tech.');
      return Tech.html5;
    }

    if (plugins?.kollective) {
      logger.info('Using Kollective SDK for P2P streaming.');
      return Tech.kollective;
    }
    if (plugins?.ramp) {
      logger.info('Using VBrick for P2P streaming.');
      return Tech.vbrick;
    }
    if (plugins?.hive) {
      logger.info('Using Hive for P2P streaming.');
      return Tech.hive;
    }

    logger.info('Using HTML5 tech for video playback.');
    return Tech.html5;
  }, [sources.hls, plugins?.kollective, plugins?.ramp, plugins?.hive, logger]);

  const kollective = useKollective(tech === Tech.kollective, player, setVideoSrc, plugins.kollective, sources.hls);
  const vBrick = useVBrick(tech === Tech.vbrick, player, setVideoSrc, plugins.ramp, sources.hls);

  // get sources for hive plugins and handle the case no p2p plugin is enabled
  // Note: refactor to be hooks
  useEffect(() => {
    if (tech === Tech.kollective || tech === Tech.vbrick) {
      return;
    }

    const abortController = new AbortController();

    getSources(player, sources, plugins, abortController.signal, preferredQuality)
      .then((videoSrc) => {
        if (abortController.signal.aborted) {
          return;
        }

        return setVideoSrc(videoSrc);
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }

        logger.error('Error getting video sources:', error);
        if (error instanceof PlayerError) {
          player.error(error.message);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [sources, plugins, preferredQuality, autoplay, player, tech, setVideoSrc, logger]);

  const reloadSource = useCallback(() => {
    if (tech === Tech.kollective) {
      kollective.reloadSource();
    } else if (tech === Tech.vbrick) {
      vBrick.reloadSource();
    } else {
      setVideoSrc(player.currentSources(), true);
    }
  }, [kollective, player, setVideoSrc, tech, vBrick]);

  return { sourceIsSet, reloadSource };
}
